export type GuidLike = string | Guid;

const guidRegex: RegExp = /^[a-f0-9]{8}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[a-f0-9]{12}$/i;
const emptyGuidValue = '00000000-0000-0000-0000-000000000000';

/**
 * This is where the magic happens. Generates the specified number of GUID quads using a random number.
 * @param count The number of quads to generate.
 */
function generateQuad(count: number = 1): string {
  let out = '';
  for (let i = 0; i < count; i++) {
    // tslint:disable-next-line:no-bitwise
    out += (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  }

  return out;
}

/**
 * Represents a Globally Unique Identifier (GUID). This is not meant to be cryptographically secure.
 */
export class Guid {
  /**
   * Represents a placeholder GUID or a value to represent a non-GUID.
   * Returns '00000000-0000-0000-0000-000000000000'.
   */
  public static readonly EMPTY: Guid = new Guid(emptyGuidValue);

  private readonly value: string;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Constructor

  private constructor(value: string) {
    this.value = value.toLowerCase();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Properties

  /**
   * Read-only formatted value of the GUID.
   */
  public get formattedValue(): string {
    return this.value;
  }

  /**
   * Returns a value indicating whether this GUID is empty (equal to Guid.EMPTY).
   */
  public get isEmpty(): boolean {
    return this.value === emptyGuidValue;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Methods

  /**
   * Returns a value indicating whether the value is a valid GUID.
   * @param value Either a Guid instance or a string
   */
  public static isGuid(value: GuidLike): boolean {
    return (value instanceof Guid) || guidRegex.test(value);
  }

  /**
   * Creates a new GUID.
   */
  public static create(): Guid {
    return new Guid([generateQuad(2), generateQuad(), generateQuad(), generateQuad(), generateQuad(3)].join('-'));
  }

  /**
   * Parses an existing GUID string. Throws a TypeError if the value is not a valid GUID.
   * @param value the existing GUID string to parse
   */
  public static parse(value: string): Guid {
    if (!Guid.isGuid(value)) {
      throw new TypeError(`Value is not a valid GUID: '${value}'`);
    }

    return new Guid(value);
  }

  /**
   * Returns the formatted GUID.
   */
  public toString(): string {
    return this.value;
  }

  /**
   * Returns a value indicating whether another GUID is equal to this GUID.
   * @param other Either another Guid instance or a formatted GUID string.
   */
  public equals(other: GuidLike): boolean {
    const otherGuid = (other instanceof Guid) ? other : Guid.parse(other);
    return this.formattedValue === otherGuid.formattedValue;
  }
}
